import { getCurrencySymbol } from "@common/helpers/currency";
import { WeightFormatter } from "@common/helpers/WeightFormatter";
import type { CompleteTaskParams } from "@shared/api";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "apiClient";
import { CircleX } from "lucide-react";
import { Suspense, useCallback, useMemo, useRef, useState } from "react";

import CompletionForm from "components/CompletionModal/CompletionForm";
import Modal from "components/core/Modal";
import { SkeletonText } from "components/core/Skeleton";
import { useIsMobile } from "hooks";

import { CompletionModalContext } from "./Context";
import type { OpenCompletionModalParams } from "./Context";

export const CompletionModalProvider = ({ children }: { children: React.ReactNode }) => {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [params, setParams] = useState<OpenCompletionModalParams | null>(null);

  const snapshotRef = useRef<{ showInventory: boolean; showHarvestRecord: boolean }>({
    showInventory: false,
    showHarvestRecord: false
  });

  const onSuccessRef = useRef<(() => void) | undefined>(undefined);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const { mutate: completeWithDetails, isPending: isSubmitting } = useMutation({
    mutationFn: apiClient.tasks.completeTask,
    onSuccess: () => {
      close();
      onSuccessRef.current?.();
    }
  });

  const openCompletionModal = useCallback((nextParams: OpenCompletionModalParams) => {
    snapshotRef.current = {
      showInventory: nextParams.showInventory,
      showHarvestRecord: nextParams.showHarvestRecord
    };
    onSuccessRef.current = nextParams.onSuccess;
    setParams(nextParams);
    setIsOpen(true);
  }, []);

  const handleConfirm = (data: Omit<CompleteTaskParams, "id">) => {
    if (!params) return;

    completeWithDetails({
      id: params.task.id,
      ...data,
      completedAt: data.completedAt ?? params.selectedDate
    });
  };

  const formatter = useMemo(
    () => (params?.unitPreference ? WeightFormatter.build(params.unitPreference) : null),
    [params?.unitPreference]
  );

  const currencyPrefix = params ? getCurrencySymbol(params.currencyPreference) : "";

  return (
    <CompletionModalContext.Provider value={{ openCompletionModal }}>
      {children}
      {params && (
        <Modal
          closable
          destroyOnHidden
          closeIcon={<CircleX size="1em" />}
          data-testid="completion-modal"
          footer={null}
          layout={isMobile ? "fullScreen" : "compact"}
          open={isOpen}
          title={`Complete: ${params.task.name} - ${params.task.cropName}`}
          width={768}
          onCancel={close}
        >
          <Suspense fallback={<SkeletonText />}>
            <CompletionForm
              currencyPreference={params.currencyPreference}
              currencyPrefix={currencyPrefix}
              formatter={formatter!}
              isHarvesting={params.task.taskType === "harvesting"}
              isOverdue={params.isOverdue}
              isSubmitting={isSubmitting}
              selectedDate={params.selectedDate}
              showHarvestRecord={
                isOpen ? snapshotRef.current.showHarvestRecord : params.showHarvestRecord
              }
              showInventory={
                isOpen ? snapshotRef.current.showInventory : params.showInventory
              }
              task={params.task}
              unitPreference={params.unitPreference}
              onCancel={close}
              onConfirm={handleConfirm}
            />
          </Suspense>
        </Modal>
      )}
    </CompletionModalContext.Provider>
  );
};
